import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

const COLORS = ['#4A6741', '#3B82F6', '#6366F1', '#F59E0B', '#EC4899', '#8B5CF6', '#14B8A6', '#F97316', '#EF4444', '#6B7280'];

interface ChartRendererProps {
  type: 'bar' | 'line' | 'pie' | 'composed' | 'table';
  data: Record<string, unknown>[];
  measures: string[];
  dimensions: string[];
}

function shortLabel(key: string): string {
  // "ad_performance.total_spend" → "Total Spend"
  const name = key.split('.').pop() || key;
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/^Total /, '')
    .replace(/^Sum /, '');
}

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return '—';
  const n = Number(val);
  if (isNaN(n)) return String(val);
  if (Math.abs(n) >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}k`;
  if (n % 1 !== 0) return n.toFixed(2);
  return String(n);
}

export default function ChartRenderer({ type, data, measures, dimensions }: ChartRendererProps) {
  if (!data || data.length === 0) {
    return <p className="text-xs text-stone-400 italic">No data returned</p>;
  }

  const xKey = dimensions[0] || Object.keys(data[0])[0];

  // Clean data: convert string numbers and shorten labels
  const cleanData = data.map((row) => {
    const clean: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(row)) {
      const numVal = Number(val);
      clean[key] = !isNaN(numVal) && val !== null && val !== '' ? numVal : val;
    }
    // Shorten the X-axis label if it's a dimension
    if (clean[xKey] && typeof clean[xKey] === 'string') {
      const str = clean[xKey] as string;
      clean[xKey] = str.length > 25 ? str.slice(0, 22) + '...' : str;
    }
    return clean;
  });

  if (type === 'table') {
    const cols = Object.keys(data[0]);
    return (
      <div className="overflow-x-auto max-h-[250px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-stone-100 dark:bg-[#1e2520] sticky top-0">
            <tr>
              {cols.map((col) => (
                <th key={col} className="px-2 py-1 text-left font-medium text-stone-600 dark:text-stone-400 whitespace-nowrap">
                  {shortLabel(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
            {data.slice(0, 20).map((row, i) => (
              <tr key={i}>
                {cols.map((col) => (
                  <td key={col} className="px-2 py-1 whitespace-nowrap">{formatValue(row[col])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === 'pie') {
    const nameKey = dimensions[0] || Object.keys(data[0])[0];
    const valueKey = measures[0] || Object.keys(data[0])[1];
    return (
      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie
            data={cleanData}
            cx="50%"
            cy="50%"
            innerRadius={40}
            outerRadius={80}
            paddingAngle={2}
            dataKey={valueKey}
            nameKey={nameKey}
            label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
          >
            {cleanData.map((_, i) => (
              <Cell key={i} fill={COLORS[i % COLORS.length]} />
            ))}
          </Pie>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Tooltip formatter={((v: number) => formatValue(v)) as any} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (type === 'line') {
    return (
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={cleanData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xKey} tick={{ fontSize: 10 }} tickFormatter={(v: string) => typeof v === 'string' && v.length > 10 ? v.slice(5, 10) : v} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => formatValue(v)} />
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Tooltip formatter={((v: number) => formatValue(v)) as any} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {measures.map((m, i) => (
            <Line key={m} dataKey={m} name={shortLabel(m)} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (type === 'composed') {
    return (
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={cleanData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xKey} tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => formatValue(v)} />
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Tooltip formatter={((v: number) => formatValue(v)) as any} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {measures.slice(0, 1).map((m, i) => (
            <Bar key={m} dataKey={m} name={shortLabel(m)} fill={COLORS[i]} opacity={0.8} />
          ))}
          {measures.slice(1).map((m, i) => (
            <Line key={m} dataKey={m} name={shortLabel(m)} stroke={COLORS[i + 1]} strokeWidth={2} dot={false} />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  // Default: bar chart
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={cleanData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xKey} tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => formatValue(v)} />
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <Tooltip formatter={((v: number) => formatValue(v)) as any} />
        <Legend wrapperStyle={{ fontSize: 10 }} />
        {measures.map((m, i) => (
          <Bar key={m} dataKey={m} name={shortLabel(m)} fill={COLORS[i % COLORS.length]} radius={[2, 2, 0, 0]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
